import type { SiteEntity } from '../../types/site';
import type { SiteDocument, SiteRagReport, DataSource } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import {
  demonstrationSiteProvenanceV1Schema,
  demonstrationDocumentProvenanceV1Schema,
  frozenDemonstrationBasisV1Schema,
  FICTIONAL_DEMONSTRATION_NOTICE,
  type DemonstrationDisclosureV1,
  type DemonstrationSiteProvenanceV1,
  type FrozenDemonstrationBasisV1,
} from '../../types/demonstration';
import { canonicalJson, hashCanonicalJson, sha256Hex } from '../reportGeneration/reportContractHash';
import { docsEligibleForEvidence } from '../evidenceDocEligibility';
import { documentBodyText } from '../documentBody';

const fictionalMarker = /\bFICTIONAL\s+DEMONSTRATION\b/i;
export function isFictionalSourceDocument(doc: Pick<SiteDocument, 'demonstrationProvenance' | 'fileName' | 'textContent' | 'fullExtractedText'>): boolean {
  return doc.demonstrationProvenance !== undefined || fictionalMarker.test(doc.fileName)
    || fictionalMarker.test(documentBodyText(doc).slice(0, 8_000));
}

export function demonstrationDisclosure(provenance: { scenarioId: string }): DemonstrationDisclosureV1 {
  return { schemaVersion: 1, kind: 'fictional-scenario', scenarioId: provenance.scenarioId, notice: FICTIONAL_DEMONSTRATION_NOTICE };
}

/** A name is disclosure, not authority. Only the validated server stamp confers demo status. */
export function requireDemonstrationSite(site: Pick<SiteEntity, 'id' | 'name' | 'createdBy' | 'demonstrationProvenance'>, ownerUid?: string): DemonstrationSiteProvenanceV1 | undefined {
  if (site.demonstrationProvenance === undefined) return undefined;
  const provenance = demonstrationSiteProvenanceV1Schema.parse(site.demonstrationProvenance);
  if (!site.id.startsWith('site-demo-')) {
    throw new Error('Demonstration must use a distinct canonical demo site identity.');
  }
  if ((ownerUid && provenance.ownerUid !== ownerUid) || site.createdBy?.uid !== provenance.ownerUid) {
    throw new Error('Fictional demonstration owner does not match its canonical site.');
  }
  return provenance;
}

/** Intake preflight and terminal transaction share exactly the same isolation fence. */
export function assertDemonstrationIntake(site: SiteEntity, docs: readonly SiteDocument[], operatorUid: string, ownerUid: string): void {
  const provenance = requireDemonstrationSite(site, ownerUid);
  if (provenance && operatorUid !== ownerUid) throw new Error('Fictional demonstration intake requires the enabled canonical owner.');
  for (const doc of docs) {
    if (!provenance) {
      if (isFictionalSourceDocument(doc)) throw new Error('Fictional source cannot be attached to an original real site.');
      continue;
    }
    if (doc.demonstrationProvenance === undefined) {
      if (isFictionalSourceDocument(doc)) throw new Error('Fictional source is missing its server-authored provenance.');
      continue; // independently sourced public-context documents stay ordinary sources
    }
    const source = demonstrationDocumentProvenanceV1Schema.parse(doc.demonstrationProvenance);
    if (source.scenarioId !== provenance.scenarioId) throw new Error('Fictional document scenario does not match the demo site.');
    if (source.bodySha256 !== sha256Hex(documentBodyText(doc))) throw new Error('Fictional extracted-source body hash mismatch.');
    if (source.originalFileSha256 !== doc.contentHash) throw new Error('Fictional original-file hash does not match the document metadata.');
  }
}

/** Bind extraction only after the ordinary extractor has retained the source text. */
export function stampFictionalDocument(doc: SiteDocument, site: SiteEntity, operatorUid: string, ownerUid: string): SiteDocument {
  const provenance = requireDemonstrationSite(site, ownerUid);
  if (!provenance || operatorUid !== ownerUid) throw new Error('Fictional document stamping requires the canonical demo owner.');
  const stamped: SiteDocument = { ...doc, demonstrationProvenance: {
    ...demonstrationDisclosure(provenance),
    bodySha256: sha256Hex(documentBodyText(doc)),
    ...(doc.contentHash ? { originalFileSha256: doc.contentHash } : {}),
  } };
  assertDemonstrationIntake(site, [stamped], operatorUid, ownerUid);
  return stamped;
}

/** Exact stored-body identity; a same-length preview or old process cache is insufficient. */
export function verifyDemonstrationStoredDocument(metadata: SiteDocument, payload: Partial<SiteDocument>): SiteDocument {
  if (payload.id !== metadata.id || payload.contentHash !== metadata.contentHash) throw new Error('Demo document stored source identity mismatch.');
  if (canonicalJson(payload.demonstrationProvenance) !== canonicalJson(metadata.demonstrationProvenance)) {
    throw new Error('Demo document stored provenance differs from current metadata.');
  }
  const body = payload.fullExtractedText ?? payload.textContent;
  if (typeof body !== 'string' || body.length === 0 || (metadata.extractedCharsKept ?? 0) > body.length) {
    throw new Error('Demo document full extracted-source read is missing or incomplete.');
  }
  // Stored bytes supply the body only; current inclusion/curation metadata remains authoritative.
  const hydrated = { ...metadata, fullExtractedText: body,
    ...(payload.topicSegments ? { topicSegments: payload.topicSegments } : {}),
    ...(payload.visualReadings ? { visualReadings: payload.visualReadings } : {}) };
  if (metadata.demonstrationProvenance) {
    const source = demonstrationDocumentProvenanceV1Schema.parse(metadata.demonstrationProvenance);
    if (source.bodySha256 !== sha256Hex(body) || source.originalFileSha256 !== metadata.contentHash) {
      throw new Error('Demo document extracted-source or original-file hash mismatch.');
    }
  } else if (isFictionalSourceDocument(hydrated)) {
    throw new Error('Fictional source is missing its authoritative provenance.');
  }
  return hydrated;
}

/** Caller supplies freshly owner-hydrated bodies, never site-record previews. */
export function freezeDemonstrationBasis(site: SiteEntity, docs: readonly SiteDocument[], ownerUid: string, scope: 'sed' | 'report'): FrozenDemonstrationBasisV1 | undefined {
  const provenance = requireDemonstrationSite(site, ownerUid);
  assertDemonstrationIntake(site, docs, ownerUid, ownerUid);
  if (!provenance) return undefined;
  const seen = new Set<string>();
  const documents = docs.map(doc => {
    if (!doc.id || seen.has(doc.id)) throw new Error('Demo manifest document identities must be nonempty and unique.');
    seen.add(doc.id);
    const body = doc.fullExtractedText;
    if (typeof body !== 'string' || body.length === 0 || (doc.extractedCharsKept ?? 0) > body.length) {
      throw new Error('Demo manifest requires a complete stored-source body read.');
    }
    return { documentId: doc.id, bodySha256: sha256Hex(body),
      ...(doc.contentHash ? { originalFileSha256: doc.contentHash } : {}),
      sourceKind: doc.demonstrationProvenance ? 'fictional-scenario' as const : 'public-context' as const };
  }).sort((a, b) => a.documentId < b.documentId ? -1 : a.documentId > b.documentId ? 1 : 0);
  return frozenDemonstrationBasisV1Schema.parse({ ...demonstrationDisclosure(provenance), ownerUid, scope,
    documents, manifestHash: hashCanonicalJson(documents) });
}

/** Recheck live metadata at commit; body verification happened against the stored source bytes. */
export function assertFrozenDemonstrationBasis(basis: FrozenDemonstrationBasisV1 | undefined, site: SiteEntity, docs: readonly SiteDocument[], ownerUid: string, scope: 'sed' | 'report'): void {
  const provenance = requireDemonstrationSite(site, ownerUid);
  if (!provenance) {
    if (basis || docs.some(isFictionalSourceDocument)) throw new Error('Fictional basis cannot bind to an original real site.');
    return;
  }
  const frozen = frozenDemonstrationBasisV1Schema.parse(basis);
  if (frozen.ownerUid !== ownerUid || frozen.scenarioId !== provenance.scenarioId || frozen.scope !== scope
    || frozen.manifestHash !== hashCanonicalJson(frozen.documents)) throw new Error('Demo frozen source manifest identity mismatch.');
  const sortedIds = [...new Set(docs.map(doc => doc.id))].sort();
  if (canonicalJson(frozen.documents.map(row => row.documentId)) !== canonicalJson(sortedIds)) {
    throw new Error('Demo frozen source manifest is missing, stale or includes another document selection.');
  }
  const byId = new Map(docs.map(doc => [doc.id, doc]));
  for (const row of frozen.documents) {
    const doc = byId.get(row.documentId)!;
    if (doc.deletedAt || doc.archived) throw new Error('Demo frozen document was deleted or archived.');
    if (row.sourceKind !== (doc.demonstrationProvenance ? 'fictional-scenario' : 'public-context')) {
      throw new Error('Demo frozen document source kind mismatch.');
    }
    if (row.originalFileSha256 !== doc.contentHash) {
      throw new Error('Demo frozen document source identity changed.');
    }
    if (doc.demonstrationProvenance) {
      const source = demonstrationDocumentProvenanceV1Schema.parse(doc.demonstrationProvenance);
      if (source.scenarioId !== frozen.scenarioId) throw new Error('Demo document scenario mismatch.');
      if (source.bodySha256 !== row.bodySha256) throw new Error('Demo document scenario or extracted-source hash changed.');
    }
  }
}

/** Internal scenario identity never changes an extracted source's authority.
 * Ordinary parsing, explicit assumptions and source qualifications remain owned
 * by their normal evidence producers; private document/SED/report manifests
 * continue binding the exact canonical owner, scenario and stored bytes. */
export function qualifyFictionalDocumentEvidence(_sed: SiteEvidenceDocument, docs: readonly SiteDocument[]): void {
  for (const doc of docs) {
    if (doc.demonstrationProvenance) demonstrationDocumentProvenanceV1Schema.parse(doc.demonstrationProvenance);
  }
}

/** Reference authority comes from its actual source and qualification, not the
 * internal scenario stamp. Keep the original uploaded-document source label. */
export function qualifyFictionalReference(reference: DataSource, doc: SiteDocument): DataSource {
  if (doc.demonstrationProvenance) demonstrationDocumentProvenanceV1Schema.parse(doc.demonstrationProvenance);
  return reference;
}

/** Both source subsets become private frozen report content before sourceHash.
 * This never broadens the generator's directly selected documents. */
export function bindDemonstrationReportSources(report: SiteRagReport, directBasis: FrozenDemonstrationBasisV1, sedBasis: FrozenDemonstrationBasisV1): SiteRagReport {
  const direct = frozenDemonstrationBasisV1Schema.parse(directBasis);
  const sed = frozenDemonstrationBasisV1Schema.parse(sedBasis);
  if (direct.scope !== 'report' || sed.scope !== 'sed' || direct.ownerUid !== sed.ownerUid || direct.scenarioId !== sed.scenarioId) {
    throw new Error('Fictional report and SED source manifests do not share their exact scenario/owner/scope.');
  }
  return { ...report, demonstrationProvenance: direct, demonstrationSedProvenance: sed };
}

export function assertDemonstrationReportAtCommit(report: SiteRagReport, site: SiteEntity, ownerUid: string): void {
  const selected = new Set(report.documentIds ?? []);
  if (site.demonstrationProvenance && report.demonstrationProvenance
    && canonicalJson([...selected].sort()) !== canonicalJson(report.demonstrationProvenance.documents.map(row => row.documentId))) {
    throw new Error('Demo report document selection does not match its frozen source manifest.');
  }
  const selectedDocuments = (site.documents ?? []).filter(doc => selected.has(doc.id));
  assertFrozenDemonstrationBasis(report.demonstrationProvenance, site, selectedDocuments, ownerUid, 'report');
  if (!site.demonstrationProvenance && report.demonstrationSedProvenance) throw new Error('Fictional SED basis cannot bind to an original real site.');
  if (site.demonstrationProvenance) assertFrozenDemonstrationBasis(report.demonstrationSedProvenance, site, docsEligibleForEvidence(site), ownerUid, 'sed');
}
